import Spinner from "@/components/Spinner";
import QuestionMCQ from "@/components/quiz/components/QuestionMCQ";
import useHomeworkHandler from "@/components/quiz/hooks/useHomeworkHandler";
import { AssessmentMode, LessonDataI } from "@/components/quiz/typesAndEnums";
import { Button } from "@/components/ui/button";
import { Card, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { LessonScreenI } from "@/hooks/types";
import { getHostname } from "@/scripts/getHostname";
import { useAppSelector } from "@/store/hooks";
import { ArrowLeft, ChevronLeft, ChevronRight } from "lucide-react";
import { useState } from "react";
import QuestionGrid from "./QuestionGrid";

const LessonHomework = ({
  lessonData,
  setScreenContent,
}: {
  lessonData: LessonDataI;
  setScreenContent: React.Dispatch<React.SetStateAction<LessonScreenI>>;
}) => {
  const {
    score,
    maxScore,
    percentage,
    passed,
    isEnded,
    isStarted,
    questions,
    isSubmitPending,
    submitError,
    currentTeacher,
    handleSubmitHomework,
  } = useHomeworkHandler({ lessonData });

  const { currentHwProgress } = useAppSelector((state) => state.store);

  const [currentIndex, setCurrentIndex] = useState<number>(0);

  if (!lessonData || !questions || !isStarted)
    return (
      <div className="h-[calc(100vh-200px)] w-full flex items-center justify-center">
        <Spinner />
      </div>
    );

  const handleNext = () => {
    if (currentIndex < questions.length - 1) {
      setCurrentIndex(currentIndex + 1);
    }
  };

  const handlePrevious = () => {
    if (currentIndex > 0) {
      setCurrentIndex(currentIndex - 1);
    }
  };

  const progressPercent = isEnded
    ? percentage
    : (Object.keys(currentHwProgress ?? {}).length / questions.length) * 100;

  const calculateGridBoxClassName = (question, index) => {
    const answer = currentHwProgress?.[question?.question_id];
    const correctAnswer = question?.correct_answer;

    if (index === currentIndex)
      return "bg-blue-600 text-white hover:bg-blue-600";

    if (isEnded) {
      if (!answer) return "bg-slate-100 text-slate-600 hover:bg-slate-200";
      if (answer !== correctAnswer)
        return "bg-red-500 border border-red-500 text-white";
      if (answer === correctAnswer)
        return "bg-green-600 border border-green-600 text-white";
    } else {
      return answer
        ? "bg-slate-300 text-slate-600 hover:bg-slate-400"
        : "bg-slate-100 text-slate-600 hover:bg-slate-200";
    }

    return "";
  };

  const isSubmitDisabled =
    isSubmitPending ||
    isEnded ||
    (lessonData.must_answer_all &&
      Object.keys(currentHwProgress ?? {}).length < questions.length);

  if (!lessonData.hw_id)
    return (
      <div className="p-5 flex">
        {currentTeacher === "mr-hazem-el-kashif" ||
        getHostname().includes("kashif") ? (
          <h2 className="m-auto">الواجب في الWorkbook</h2>
        ) : (
          <h2 className="m-auto">لا يوجد واجب</h2>
        )}
      </div>
    );

  if (!isStarted)
    return (
      <div className="h-[calc(100vh-200px)] w-full flex items-center justify-center">
        <Spinner />
      </div>
    );

  return (
    <div className="min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pb-8 pt-4">
        {/* Info Alert */}
        <Button
          className="bg-primary rounded-xl w-fit mb-4 cursor-pointer"
          onClick={() =>
            setScreenContent((prev: LessonScreenI) => ({
              ...prev,
              elementId: "",
            }))
          }
        >
          <ArrowLeft className="text-white size-5 mx-3" />
        </Button>

        {isStarted && !isEnded && (
          <div className="px-4 py-2 border border-blue-300 bg-blue-50 text-blue-600 mb-4 rounded-xl">
            يتم حفظ الإجابات تلقائياً على جهازك. يمكنك العودة لإكمال الواجب في
            أي وقت من <strong>نفس الجهاز</strong>.
          </div>
        )}

        {/* Header */}
        <Card className="mb-6">
          <CardHeader>
            <div className="flex justify-between items-center">
              <CardTitle className="text-2xl">{lessonData.hw_title}</CardTitle>
            </div>
            <div className="space-y-2">
              <div className="flex justify-between text-sm text-gray-600">
                {isEnded ? (
                  <span
                    className={`font-semibold ${
                      passed ? "text-green-600" : "text-red-600"
                    }`}
                  >
                    الدرجة: {score} / {maxScore}
                  </span>
                ) : (
                  <span>
                    تم حل {Object.keys(currentHwProgress ?? {}).length} من{" "}
                    {questions.length}
                  </span>
                )}
                {isEnded ? (
                  <span>تم حل %{Math.round(progressPercent)}</span>
                ) : (
                  <span>{Math.round(progressPercent)}% مكتمل</span>
                )}
              </div>
              <Progress value={progressPercent} className="w-full" />
            </div>
          </CardHeader>
        </Card>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Question Content */}
          <div className="lg:col-span-3">
            <QuestionMCQ
              key={questions[currentIndex].question_id}
              assessmentId={lessonData?.hw_id}
              question={questions[currentIndex]}
              isEnded={isEnded}
              mustAnswerAll={lessonData.must_answer_all}
              hideCorrectAnswers={lessonData.hide_correct_answers}
              assessmentMode={AssessmentMode.HOMEWORK}
            />

            <div className="flex justify-between">
              <Button
                onClick={handlePrevious}
                disabled={currentIndex === 0}
                className="flex items-center"
              >
                <ChevronRight className="w-4 h-4 ml-2" />
                السابق
              </Button>

              <Button
                onClick={handleNext}
                disabled={currentIndex === questions.length - 1}
                className="flex items-center"
              >
                التالي
                <ChevronLeft className="w-4 h-4 mr-2" />
              </Button>
            </div>
          </div>

          {/* Question Grid */}
          <div className="lg:col-span-1">
            <QuestionGrid
              questions={questions}
              currentIndex={currentIndex}
              currentProgress={currentHwProgress}
              isEnded={isEnded}
              setCurrentIndex={setCurrentIndex}
              calculateGridBoxClassName={calculateGridBoxClassName}
              handleSubmitQuiz={handleSubmitHomework}
              isSubmitDisabled={isSubmitDisabled}
              isSubmitPending={isSubmitPending}
              SubmitButtonContent={
                isSubmitPending ? <Spinner variant="button" /> : "تسليم الواجب"
              }
            />
            {submitError && (
              <div className="alert alert-danger mt-4 rounded-ea" role="alert">
                حدث خطأ أثناء تسليم الواجب. تواصل مع الدعم.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default LessonHomework;
